import { useLocation } from 'react-router';
import { countryConfig } from '../../../country';
import { LocaleLink } from '../../../i18n/locale-link';
import { useLocale } from '../../../i18n/locale-context';
import { LOCALES } from '../../../i18n/index';
import { toggleLocalePath } from '../../../i18n/locale-path';
import { LOCALE_FLAGS, LOCALE_NAMES } from '../../../components/language-switcher';

interface Props {
  nav: { label: string; href: string }[];
  ctaLabel: string;
  /** Якорь (`#pricing`) остаётся обычным `<a>`; путь (например, `/catalog`,
   *  отступление 10) уходит через `LocaleLink`, чтобы не терять локаль. */
  ctaHref: string;
  onCtaClick: () => void;
}

/** Ссылка навигации: якорь остаётся якорем, путь уходит через LocaleLink. */
function NavItem({ label, href }: { label: string; href: string }) {
  const cls = 'text-[14.5px] font-semibold text-ink-soft transition-colors hover:text-ink';
  if (href.startsWith('#')) {
    return (
      <a className={cls} href={href}>
        {label}
      </a>
    );
  }
  return (
    <LocaleLink className={cls} to={href}>
      {label}
    </LocaleLink>
  );
}

/**
 * Шапка лендинга. Своя, не общая с приложением: у `AppLayout` сайдбарная
 * оболочка m16, внутри которой конверсионная страница получила бы два хедера.
 */
export function LandingHeader({ nav, ctaLabel, ctaHref, onCtaClick }: Props) {
  const { locale } = useLocale();
  const { pathname } = useLocation();
  // Mobile drops the trailing suffix (".com") from the wordmark — see the JSX
  // comment below. `brand.name` is a dotted domain-style name for every
  // country (uz: 'UzTender.com', kz: 'KazTender.com'), so splitting at the
  // LAST '.' recovers the same "UzTender" + ".com" split the UZ build always
  // rendered, without hardcoding the literal string.
  const dotIndex = countryConfig.brand.name.lastIndexOf('.');
  const brandBase =
    dotIndex === -1 ? countryConfig.brand.name : countryConfig.brand.name.slice(0, dotIndex);
  const brandSuffix = dotIndex === -1 ? '' : countryConfig.brand.name.slice(dotIndex);
  const ctaCls =
    'inline-flex min-h-[44px] flex-none items-center whitespace-nowrap rounded-lg border-[1.5px] border-brand bg-brand px-3 text-[13.5px] font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid sm:min-h-0 sm:px-4 sm:py-[9px] sm:text-sm';

  return (
    <header className="sticky top-0 z-50 border-b border-edge bg-bone-surface">
      <div className="mx-auto flex h-16 max-w-[1120px] items-center gap-2 px-5 sm:gap-4">
        {/*
          На телефоне логотип печатается без «.com»: под 640px шапка держит
          ещё переключатель языка и кнопку, и суффикс — единственные 49px,
          которые можно снять, не трогая ни функцию, ни текст. Скрыт CSS'ом,
          из разметки НЕ удаляется: страницы пререндерятся, а условный
          рендеринг вырезал бы «.com» из HTML целиком.
        */}
        <LocaleLink
          className="min-w-0 truncate text-[19px] font-extrabold tracking-[-0.02em] text-ink no-underline"
          to="/"
        >
          {brandBase}
          <span className="hidden text-brand sm:inline">{brandSuffix}</span>
        </LocaleLink>
        <nav className="ml-1.5 hidden gap-5 md:flex">
          {nav.map((item) => (
            <NavItem key={item.href} {...item} />
          ))}
        </nav>
        <div className="ml-auto flex flex-none items-center gap-2 sm:gap-3">
          {/*
            Переключатель языка ведёт на ЭТУ ЖЕ страницу в другой локали.
            `LocaleLink` умеет только префиксовать `to` активной локалью — у
            него нет пропа для абсолютного пути другой локали (проверено в
            `locale-link.tsx`), поэтому здесь обычный <a> с полной
            перезагрузкой страницы. Это нормально: страница другой локали
            пререндерена и приедет целиком.

            Отступление 4 (ревью владельца, 2026-08-18): текст «РУ»/«UZ»
            заменён на те же inline-SVG флаги, что и у переключателя на
            основном сайте (`components/language-switcher.tsx`, откуда они
            экспортированы) — только визуал общий, поведение (полная
            перезагрузка вместо client-side `navigate`) осталось прежним.
            Пункты переключателя — тач-цели: 44px по высоте на телефоне,
            на десктопе остаётся компактная пилюля макета.
          */}
          <div className="flex flex-none overflow-hidden rounded-full border border-edge text-xs font-bold">
            {LOCALES.map((l) => {
              const Flag = LOCALE_FLAGS[l];
              const active = locale === l;
              return (
                <a
                  key={l}
                  className={`inline-flex min-h-[44px] items-center gap-1.5 px-2.5 no-underline sm:min-h-0 sm:px-[11px] sm:py-1.5 ${active ? 'bg-brand-light' : ''}`}
                  href={toggleLocalePath(pathname, l)}
                  hrefLang={l === 'uz' ? 'uz' : undefined}
                  aria-label={LOCALE_NAMES[l]}
                >
                  <Flag
                    className={`h-3.5 w-5 rounded-[3px] transition-opacity ${active ? 'opacity-100' : 'opacity-55'}`}
                  />
                  <span className="sr-only">{LOCALE_NAMES[l]}</span>
                </a>
              );
            })}
          </div>
          {ctaHref.startsWith('#') ? (
            <a className={ctaCls} href={ctaHref} onClick={onCtaClick}>
              {ctaLabel}
            </a>
          ) : (
            <LocaleLink className={ctaCls} to={ctaHref} onClick={onCtaClick}>
              {ctaLabel}
            </LocaleLink>
          )}
        </div>
      </div>
    </header>
  );
}
